import { PersistedClient } from "@tanstack/react-query-persist-client";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Persister } from "@tanstack/react-query-persist-client";
import { set, del, get } from "idb-keyval";
import { ErrorResponse, MaintenanceScheduleType } from "./types";
import { toast } from "@/hooks/use-toast";
import i18next from "./i18next";
import { format, isValid, Locale, parse, startOfToday } from "date-fns";
import {
  localePathMap,
  locales,
  MaintenanceScheduleTypesMap,
  phoneCodesWithFlags,
} from "./constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Round a conversion/exchange rate to 6 decimal places (matches backend RoundRate). */
export const roundRate = (value: number): number =>
  parseFloat(value.toFixed(6));

export function createIDBPersister(idbValidKey: IDBValidKey = "reactQuery") {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, JSON.stringify(client));
    },
    restoreClient: async () => {
      const client = await get<string>(idbValidKey);
      return client ? JSON.parse(client) : undefined;
    },
    removeClient: async () => {
      await del(idbValidKey);
    },
  } as Persister;
}

export const getErrorMessage = (error: ErrorResponse) => {
  if (!error.response?.data) {
    return i18next.t("error_message.something_went_wrong");
  }

  if ("errors" in error.response.data) {
    return error.response.data.errors?.join(", ") ?? "";
  } else if ("message" in error.response.data) {
    return error.response.data.message;
  }

  return i18next.t("error_message.something_went_wrong");
};

export const showErrorToast = (error: ErrorResponse) => {
  const message = getErrorMessage(error);
  toast({
    title: i18next.t("error_message.title"),
    description: message,
    variant: "destructive",
  });
};

export const currencyFormatter = (
  amount: number | undefined,
  currency: string = "EUR"
) => {
  const locale = i18next.language;
  if (
    (typeof amount !== "number" || isNaN(amount) || !amount) &&
    amount !== 0
  ) {
    return "-";
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount);
};

export function formatDate(
  date: Date | string,
  dateFormat = "PP",
  locale?: Locale
) {
  // If input is a string, parse it as a local date to avoid timezone issues
  const dateObj = typeof date === "string" ? parseLocalDate(date) : date;

  return format(dateObj, dateFormat, {
    locale:
      locale ??
      locales[
        localePathMap[
          i18next.language as keyof typeof localePathMap
        ] as keyof typeof locales
      ],
  });
}

export const getDatesInRange = (startDate: Date, endDate: Date): Date[] => {
  const dates: Date[] = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

export const getFlagByName = (name: string) => {
  if (!name) return "🏳️";
  const flag = phoneCodesWithFlags.find(
    (item) => item.id.toLowerCase() === name.toLowerCase()
  );
  return flag?.flag ?? "🏳️";
};

export const formatDateToISOString = (date: Date) => {
  const tzoffset = date.getTimezoneOffset() * 60000; //offset in milliseconds
  return new Date(date.getTime() - tzoffset).toISOString();
};

export const getFirstDayOfMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

export const getLastDayOfMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Converts a Date to YYYY-MM-DD string without timezone shift.
 * Use for date-only fields: DueDate, IssueDate, TransactionDate, etc.
 */
export const toDateOnlyString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parses an ISO date string to a local Date (without timezone shift).
 * Use when receiving date-only fields from the API.
 */
export const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Parses the `?date=` (yyyy-MM-dd) search param as a local date. Falls back to
 * today when it's missing or not a real calendar day (e.g. 2026-02-30).
 * `new Date("yyyy-MM-dd")` is UTC midnight, the previous day west of UTC.
 */
export const parseDateParam = (param: string | null): Date => {
  const date =
    param && /^\d{4}-\d{2}-\d{2}$/.test(param)
      ? parse(param, "yyyy-MM-dd", new Date())
      : undefined;
  return date && isValid(date) ? date : startOfToday();
};

export const isDateBetween = (date: Date, startDate: Date, endDate: Date) => {
  return date >= startDate && date <= endDate;
};

export const getMaintenanceScheduleTypeTranslationKey = (
  scheduleType: MaintenanceScheduleType
): string => {
  return (
    "maintenance_schedule_type." + MaintenanceScheduleTypesMap[scheduleType] ||
    scheduleType
  );
};
